import React, { useState } from 'react';
import { 
  ChevronLeft, 
  ChevronRight, 
  ChevronsLeft, 
  ChevronsRight, 
  Search, 
  SortAsc, 
  SortDesc, 
  Download, 
  Filter, 
  X 
} from 'lucide-react';
import * as XLSX from 'xlsx';

interface Column {
  header: string;
  accessorKey: string;
  cell?: (info: any) => React.ReactNode;
  enableSorting?: boolean;
}

interface AdminDataTableProps {
  data: any[];
  columns: Column[];
  title?: string;
  subtitle?: string;
  loading?: boolean;
  pagination?: {
    pageIndex: number;
    pageSize: number;
    pageCount: number;
    onPageChange: (page: number) => void;
  };
  sorting?: {
    field: string | null;
    order: 'asc' | 'desc' | null;
    onSortingChange: (field: string, order: 'asc' | 'desc') => void;
  };
  filtering?: {
    globalFilter: string;
    onGlobalFilterChange: (value: string) => void;
  };
  rowSelection?: {
    selectedRows: Record<string, boolean>;
    onRowSelectionChange: (rowId: string, value: boolean) => void;
  };
  actions?: React.ReactNode;
  enableExport?: boolean;
  exportFileName?: string;
}

export default function AdminDataTable({
  data,
  columns,
  title,
  subtitle,
  loading = false,
  pagination,
  sorting,
  filtering,
  rowSelection,
  actions,
  enableExport = false,
  exportFileName = 'export'
}: AdminDataTableProps) {
  const [showFilters, setShowFilters] = useState(false);

  // Handle export to Excel
  const handleExport = () => {
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Data");
    XLSX.writeFile(workbook, `${exportFileName}.xlsx`);
  };

  // Handle sorting
  const handleSort = (field: string) => {
    if (!sorting) return;
    
    const currentOrder = sorting.field === field ? sorting.order : null;
    let newOrder: 'asc' | 'desc' | null = null;
    
    if (currentOrder === null) {
      newOrder = 'asc';
    } else if (currentOrder === 'asc') {
      newOrder = 'desc';
    } else {
      newOrder = null;
    }
    
    sorting.onSortingChange(field, newOrder as 'asc' | 'desc');
  };

  // Handle row selection
  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!rowSelection) return;
    
    const value = e.target.checked;
    const newSelection: Record<string, boolean> = {};
    
    data.forEach((row, index) => {
      newSelection[index.toString()] = value;
    });
    
    // Apply to all rows
    Object.keys(newSelection).forEach(rowId => {
      rowSelection.onRowSelectionChange(rowId, newSelection[rowId]);
    });
  };

  // Calculate if all rows are selected
  const allRowsSelected = rowSelection && data.length > 0 && 
    Object.keys(rowSelection.selectedRows).length === data.length && 
    Object.values(rowSelection.selectedRows).every(Boolean);

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
      {/* Header */}
      {(title || filtering || actions || enableExport) && (
        <div className="p-6 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            {/* Title and subtitle */}
            {(title || subtitle) && (
              <div>
                {title && <h3 className="text-lg font-semibold text-gray-900">{title}</h3>}
                {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
              </div>
            )}
            
            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3">
              {/* Search */}
              {filtering && (
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Buscar..."
                    value={filtering.globalFilter || ''}
                    onChange={(e) => filtering.onGlobalFilterChange(e.target.value)}
                    className="pl-9 pr-4 py-2 w-full sm:w-auto border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-brand-teal text-sm"
                  />
                  {filtering.globalFilter && (
                    <button
                      onClick={() => filtering.onGlobalFilterChange('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              )}
              
              {/* Filter toggle */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`px-3 py-2 border rounded-lg flex items-center gap-2 text-sm ${
                  showFilters 
                    ? 'bg-brand-light text-brand-teal border-brand-teal' 
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Filter className="h-4 w-4" />
                <span>Filtros</span>
              </button>
              
              {/* Export */}
              {enableExport && (
                <button
                  onClick={handleExport}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center gap-2 text-sm"
                >
                  <Download className="h-4 w-4" />
                  <span>Exportar</span>
                </button>
              )}
              
              {/* Custom actions */}
              {actions}
            </div>
          </div>
          
          {/* Filters */}
          {showFilters && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                {/* Filter fields would go here */}
                <div className="text-sm text-gray-500">
                  Filtros avançados serão implementados de acordo com as necessidades específicas de cada tabela.
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              {/* Checkbox column */}
              {rowSelection && (
                <th className="px-6 py-3 text-left">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={allRowsSelected}
                      onChange={handleSelectAll}
                      className="rounded border-gray-300 text-brand-teal focus:ring-brand-teal"
                    />
                  </div>
                </th>
              )}
              
              {/* Data columns */}
              {columns.map((column, index) => (
                <th 
                  key={index} 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  {column.enableSorting && sorting ? (
                    <button
                      className="flex items-center gap-1 hover:text-gray-700"
                      onClick={() => handleSort(column.accessorKey)}
                    >
                      {column.header}
                      {sorting.field === column.accessorKey ? (
                        sorting.order === 'asc' ? (
                          <SortAsc className="h-4 w-4" />
                        ) : (
                          <SortDesc className="h-4 w-4" />
                        )
                      ) : (
                        <div className="w-4 h-4" /> // Empty space for alignment
                      )}
                    </button>
                  ) : (
                    column.header
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {loading ? (
              <tr>
                <td 
                  colSpan={columns.length + (rowSelection ? 1 : 0)} 
                  className="px-6 py-12 text-center"
                >
                  <div className="flex flex-col items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-teal mb-4"></div>
                    <p className="text-gray-500">Carregando dados...</p>
                  </div>
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td 
                  colSpan={columns.length + (rowSelection ? 1 : 0)} 
                  className="px-6 py-12 text-center"
                >
                  <p className="text-gray-500">Nenhum dado encontrado</p>
                </td>
              </tr>
            ) : (
              data.map((row, rowIndex) => (
                <tr 
                  key={rowIndex} 
                  className="hover:bg-gray-50"
                >
                  {/* Checkbox column */}
                  {rowSelection && (
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={rowSelection.selectedRows[rowIndex.toString()] || false}
                          onChange={(e) => rowSelection.onRowSelectionChange(rowIndex.toString(), e.target.checked)}
                          className="rounded border-gray-300 text-brand-teal focus:ring-brand-teal"
                        />
                      </div>
                    </td>
                  )}
                  
                  {/* Data cells */}
                  {columns.map((column, colIndex) => (
                    <td key={colIndex} className="px-6 py-4 whitespace-nowrap">
                      {column.cell ? column.cell({ row, value: row[column.accessorKey] }) : row[column.accessorKey]}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      
      {/* Pagination */}
      {pagination && pagination.pageCount > 0 && (
        <div className="px-6 py-4 flex items-center justify-between border-t border-gray-200">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => pagination.onPageChange(pagination.pageIndex - 1)}
              disabled={pagination.pageIndex === 0}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Anterior
            </button>
            <button
              onClick={() => pagination.onPageChange(pagination.pageIndex + 1)}
              disabled={pagination.pageIndex === pagination.pageCount - 1}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Próximo
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Mostrando <span className="font-medium">{pagination.pageIndex * pagination.pageSize + 1}</span> a{' '}
                <span className="font-medium">
                  {Math.min((pagination.pageIndex + 1) * pagination.pageSize, data.length)}
                </span>{' '}
                de <span className="font-medium">{data.length}</span> resultados
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={() => pagination.onPageChange(0)}
                  disabled={pagination.pageIndex === 0}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Primeira</span>
                  <ChevronsLeft className="h-5 w-5" />
                </button>
                <button
                  onClick={() => pagination.onPageChange(pagination.pageIndex - 1)}
                  disabled={pagination.pageIndex === 0}
                  className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Anterior</span>
                  <ChevronLeft className="h-5 w-5" />
                </button>
                
                {/* Page numbers */}
                {Array.from({ length: Math.min(5, pagination.pageCount) }).map((_, i) => {
                  let pageNumber;
                  if (pagination.pageCount <= 5) {
                    pageNumber = i;
                  } else if (pagination.pageIndex < 3) {
                    pageNumber = i;
                  } else if (pagination.pageIndex > pagination.pageCount - 4) {
                    pageNumber = pagination.pageCount - 5 + i;
                  } else {
                    pageNumber = pagination.pageIndex - 2 + i;
                  }
                  
                  return (
                    <button
                      key={pageNumber}
                      onClick={() => pagination.onPageChange(pageNumber)}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        pagination.pageIndex === pageNumber
                          ? 'z-10 bg-brand-teal text-white border-brand-teal'
                          : 'bg-white text-gray-500 hover:bg-gray-50 border-gray-300'
                      }`}
                    >
                      {pageNumber + 1}
                    </button>
                  );
                })}
                
                <button
                  onClick={() => pagination.onPageChange(pagination.pageIndex + 1)}
                  disabled={pagination.pageIndex === pagination.pageCount - 1}
                  className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Próximo</span>
                  <ChevronRight className="h-5 w-5" />
                </button>
                <button
                  onClick={() => pagination.onPageChange(pagination.pageCount - 1)}
                  disabled={pagination.pageIndex === pagination.pageCount - 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Última</span>
                  <ChevronsRight className="h-5 w-5" />
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}